import React from 'react';

import { defineMessages, FormattedMessage } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import GlobeIcon from '@atlaskit/icon/core/globe';
import { Anchor } from '@atlaskit/primitives/compiled/anchor';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

const messages = defineMessages({
	link_list_errored_subtitle: {
		id: 'smart_knowledge_card.link_list_errored_subtitle',
		defaultMessage: 'Link',
		description:
			'Subtitle shown beneath the title of a link that could not be resolved by the Smart Link, indicating the row is a plain link.',
	},
});

const styles = cssMap({
	// Match the LinkIcon style overrides applied to resolved rows so the errored
	// row's globe tile visually matches the resolved icon exactly. The icon is
	// vertically centred against the title/subtitle stack (`alignBlock="center"`),
	// so no compensating negative margin is needed.
	iconTile: {
		display: 'flex',
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		borderRadius: token('radius.large'),
		backgroundColor: token('color.background.neutral'),
	},
	titleWrapper: {
		flex: 1,
		minWidth: '0px',
		overflow: 'hidden',
	},
	// Replicate Inline's flex layout and strip the default anchor underline
	// (including on hover, per product direction) so the errored row matches the
	// visual style of a resolved smart-card row.
	anchor: {
		display: 'flex',
		alignItems: 'center',
		gap: token('space.100'),
		textDecoration: 'none',
		width: '100%',
		'&:hover': {
			textDecoration: 'none',
		},
		'&:focus': {
			textDecoration: 'none',
		},
		'&:active': {
			textDecoration: 'none',
		},
	},
});

interface LinkListErroredItemProps {
	/**
	 * Fired on middle-click. Used for click telemetry, mirroring resolved rows.
	 */
	onAuxClick?: React.MouseEventHandler<HTMLAnchorElement>;
	/**
	 * Fired on primary click. Used for click telemetry, mirroring resolved rows.
	 * Navigation itself is handled natively by the anchor's `href`.
	 */
	onClick?: React.MouseEventHandler<HTMLAnchorElement>;
	/**
	 * Fired on right-click / context menu. Used for click telemetry.
	 */
	onContextMenu?: React.MouseEventHandler<HTMLAnchorElement>;
	/**
	 * The test id applied to the row wrapper.
	 */
	testId: string;
	/**
	 * The pre-fetched title for the URL. Falls back to the raw URL when empty.
	 */
	title?: string;
	url: string;
}

/**
 * The errored row content rendered *in place of* a Smart Card when its URL fails
 * to resolve (and the `kd_link_list_show_errored_links` gate is on). It mimics
 * the resolved (block) layout: a globe icon tile on the left, the pre-fetched
 * `title` as the primary line, and a "Link" subtitle beneath it (styled like the
 * Provider / CreatedBy subtitle on resolved rows).
 *
 * The surrounding row container (border, rounding, padding, hover) is owned by
 * the parent list item, so this component only renders the inner row content.
 *
 * Shared by both the full and compact link-list variants so the errored
 * presentation stays consistent and lives in a single place.
 */
export const LinkListErroredItem = ({
	url,
	title,
	testId,
	onClick,
	onAuxClick,
	onContextMenu,
}: LinkListErroredItemProps): React.JSX.Element => {
	return (
		<Anchor
			href={url}
			target="_blank"
			xcss={styles.anchor}
			testId={testId}
			onClick={onClick}
			onAuxClick={onAuxClick}
			onContextMenu={onContextMenu}
		>
			<Box xcss={styles.iconTile}>
				<GlobeIcon label="" color={token('color.icon')} />
			</Box>
			<Stack xcss={styles.titleWrapper} space="space.0">
				<Text maxLines={1} color="color.text.subtle">
					{title || url}
				</Text>
				<Text size="small" color="color.text.subtle">
					<FormattedMessage {...messages.link_list_errored_subtitle} />
				</Text>
			</Stack>
		</Anchor>
	);
};
